// UI strings for marking a translation's mistakes ("Merkitse virheet": app/src/translator/MarkPanel.tsx,
// translator phase 5 items 11–12), Finnish (default) and English side by side. Finnish is written in
// full and never truncated; en must have exactly the same shape.
import type { MarkPos, MarkRole } from '@valyrio/engine/types';
import { defineArea } from '../area';

type Tense = 'past' | 'present' | 'future';

/** A person's name in the genitive (Roosa → Roosan, Axel → Axelin; docs/FINNISH-STYLE.md "Nimien taivutus"). */
const fiGen = (name: string) => (/[aeiouyäöå]$/i.test(name) ? `${name}n` : `${name}in`);
const fiCount = (n: number) => `${n} ${n === 1 ? 'merkintä' : 'merkintää'}`;

export const marksFi = {
  button: 'Merkitse virheet',
  stop: 'Lopeta merkitseminen',
  hint: 'Napauta sanaa, joka meni väärin. Voit merkitä useita.',
  sourceRow: 'Alkuteksti',
  outputRow: 'Käännös',
  wholeSentence: 'Koko lause',
  wordsLabel: 'Sanat',
  marked: (n: number) => `${fiCount(n)} tässä sanassa`,
  posQ: 'Mikä sana tämä on?',
  pos: { verb: 'verbi', noun: 'substantiivi', adjective: 'adjektiivi', name: 'nimi' } as Record<MarkPos, string>,
  roleQ: 'Mikä sen tehtävä on?',
  role: { subject: 'tekijä', object: 'kohde', recipient: 'saaja', addressee: 'puhuteltava', possessor: 'omistaja', place: 'paikka' } as Record<
    MarkRole,
    string
  >,
  formQ: 'Muoto',
  tense: { past: 'mennyt aika', present: 'nykyhetki', future: 'tuleva' } as Record<Tense, string>,
  command: 'käsky',
  number: { sg: 'yksikkö', pl: 'monikko' } as Record<'sg' | 'pl', string>,
  meanQ: 'Tarkoitan sanaa…',
  meanSearch: 'Hae sanakirjasta',
  meanSearchPlaceholder: 'suomeksi, englanniksi tai valyriaksi',
  meanNone: 'Sanakirjasta ei löytynyt tätä. Kirjoita, mitä tarkoitat, niin sana menee tarkistukseen puuttuvana.',
  meanFree: 'Tarkoitan',
  meanFreeAdd: 'Lisää merkitys',
  meanChosen: (w: string) => `Tarkoitan sanaa ${w}`,
  missing: 'Puuttuu käännöksestä',
  extra: 'Ei kuulu käännökseen',
  insert: 'Tähän puuttuu sana',
  insertLabel: 'Mikä sana puuttuu tämän jälkeen?',
  shouldBe: 'Pitäisi olla',
  add: 'Lisää',
  clearWord: 'Poista tämän sanan merkinnät',
  sentenceQ: 'Koko lause on',
  question: 'kysymys',
  negative: 'kielteinen',
  you: { sg: 'sinä', pl: 'te' } as Record<'sg' | 'pl', string>,
  tone: { intimate: 'tuttavallinen', formal: 'muodollinen' } as Record<'intimate' | 'formal', string>,
  nowRight: 'Nyt oikein',
  stillWrong: 'Vieläkin väärin',
  verdictLabel: 'Onko käännös nyt oikein?',
  count: fiCount,
  send: 'Lähetä tarkistukseen',
  listHead: 'Merkinnät',
  remove: (what: string) => `Poista merkintä: ${what}`,
  // what one mark says, in a list ("”rakastan”: verbi")
  says: {
    pos: (w: string, p: string) => `”${w}”: ${p}`,
    role: (w: string, r: string) => `”${w}”: ${r}`,
    form: (w: string, f: string) => `”${w}”: ${f}`,
    meaning: (w: string, m: string) => `”${w}” = ${m}`,
    missing: (w: string) => `”${w}” puuttuu käännöksestä`,
    extra: (w: string) => `”${w}” ei kuulu käännökseen`,
    insert: (w: string, x: string) => `”${w}” jälkeen puuttuu ”${x}”`,
    insertStart: (x: string) => `alusta puuttuu ”${x}”`,
    expected: (w: string, x: string) => `”${w}” → ”${x}”`,
    sentence: (s: string) => `koko lause: ${s}`,
  },
  // marks already sent on this text (before a review session handles them)
  appliedMine: 'Käännetty merkintöjesi mukaan',
  appliedBy: (name: string) => `Käännetty ${fiGen(name)} merkintöjen mukaan`,
  showWithout: 'Näytä ilman',
  showWith: 'Käytä merkintöjä',
  expectedOurs: 'Oma käännöksenne · odottaa tarkistusta',
  noteOptional: 'Huomio (vapaaehtoinen)',
  sentMarks: (n: number) => `${fiCount(n)} lähetetty tarkistukseen.`,
  // the review queue's flag detail
  reviewHead: 'Merkinnät',
  outcome: { fixed: 'korjattu', proposal: 'oma sana ehdolla', memory: 'tallennettu omana käännöksenä', open: 'avoinna' } as Record<
    'fixed' | 'proposal' | 'memory' | 'open',
    string
  >,
};

export type MarksMessages = typeof marksFi;

const enCount = (n: number) => `${n} ${n === 1 ? 'mark' : 'marks'}`;

export const marksEn: MarksMessages = {
  button: 'Mark mistakes',
  stop: 'Stop marking',
  hint: 'Tap a word that went wrong. You can mark several.',
  sourceRow: 'Source text',
  outputRow: 'Translation',
  wholeSentence: 'Whole sentence',
  wordsLabel: 'Words',
  marked: (n) => `${enCount(n)} on this word`,
  posQ: 'What kind of word is this?',
  pos: { verb: 'verb', noun: 'noun', adjective: 'adjective', name: 'name' },
  roleQ: 'What is its role?',
  role: { subject: 'doer', object: 'object', recipient: 'recipient', addressee: 'person spoken to', possessor: 'owner', place: 'place' },
  formQ: 'Form',
  tense: { past: 'past', present: 'present', future: 'future' },
  command: 'command',
  number: { sg: 'singular', pl: 'plural' },
  meanQ: 'I mean the word…',
  meanSearch: 'Search the dictionary',
  meanSearchPlaceholder: 'in Finnish, English or High Valyrian',
  meanNone: 'Nothing found in the dictionary. Write what you mean and the word goes to the review as missing.',
  meanFree: 'I mean',
  meanFreeAdd: 'Add the meaning',
  meanChosen: (w) => `I mean ${w}`,
  missing: 'Missing from the translation',
  extra: 'Does not belong in the translation',
  insert: 'A word is missing here',
  insertLabel: 'Which word is missing after this one?',
  shouldBe: 'Should be',
  add: 'Add',
  clearWord: 'Remove the marks on this word',
  sentenceQ: 'The whole sentence is',
  question: 'a question',
  negative: 'negative',
  you: { sg: 'you (one)', pl: 'you (several)' },
  tone: { intimate: 'familiar', formal: 'formal' },
  nowRight: 'Right now',
  stillWrong: 'Still wrong',
  verdictLabel: 'Is the translation right now?',
  count: enCount,
  send: 'Send for review',
  listHead: 'Marks',
  remove: (what) => `Remove mark: ${what}`,
  says: {
    pos: (w, p) => `“${w}”: ${p}`,
    role: (w, r) => `“${w}”: ${r}`,
    form: (w, f) => `“${w}”: ${f}`,
    meaning: (w, m) => `“${w}” = ${m}`,
    missing: (w) => `“${w}” is missing from the translation`,
    extra: (w) => `“${w}” does not belong in the translation`,
    insert: (w, x) => `after “${w}”, “${x}” is missing`,
    insertStart: (x) => `“${x}” is missing at the start`,
    expected: (w, x) => `“${w}” → “${x}”`,
    sentence: (s) => `whole sentence: ${s}`,
  },
  appliedMine: 'Translated with your marks',
  appliedBy: (name) => `Translated with ${name}’s marks`,
  showWithout: 'Show without',
  showWith: 'Use the marks',
  expectedOurs: 'Your own translation · waiting for review',
  noteOptional: 'Note (optional)',
  sentMarks: (n) => `${enCount(n)} sent for review.`,
  reviewHead: 'Marks',
  outcome: { fixed: 'fixed', proposal: 'own word proposed', memory: 'saved as your own translation', open: 'open' },
};

// Messages.marks (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('marks', marksFi, marksEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof marksFi> {}
}
